import type { FC } from "react";
import { DownOutlined } from "@ant-design/icons";
import Tippy from "@tippyjs/react";
import { SIDEBAR_ROUTE_ICONS, type SidebarItem, type SidebarSectionConfig } from "../Config/SidebarConfig";

export type SidebarMode = "collapsed" | "open";

interface SidebarSectionProps {
  section: SidebarSectionConfig;
  mode: SidebarMode;
  tooltipPlacement: "left" | "right";
  isActive: boolean;
  isOpen: boolean;
  activeItem: string | null;
  openSubMenus: string[];
  onHeaderClick: (_section: SidebarSectionConfig) => void;
  onHeaderHover: () => void;
  onItemClick: (_item: SidebarItem) => void;
  onToggleSubMenu: (_name: string) => void;
}

const resolveItemIcon = (item: SidebarItem) =>
  item.route ? SIDEBAR_ROUTE_ICONS[item.route] : undefined;

const SidebarSection: FC<SidebarSectionProps> = ({
  section,
  mode,
  tooltipPlacement,
  isActive,
  isOpen,
  activeItem,
  openSubMenus,
  onHeaderClick,
  onHeaderHover,
  onItemClick,
  onToggleSubMenu,
}) => {
  const hasItems = Boolean(section.items && section.items.length > 0);

  return (
    <div className="sidebar-section">
      <Tippy disabled={mode === "open"} content={section.label} placement={tooltipPlacement}>
        <div
          className={`section-header ${isActive ? "section-active" : ""} ${mode}`}
          onClick={() => onHeaderClick(section)}
          onMouseEnter={onHeaderHover}
        >
          <span className="icon">{section.icon}</span>
          {mode === "open" && (
            <>
              <span className="label">{section.label}</span>
              {hasItems && <DownOutlined className="section-arrow" />}
            </>
          )}
        </div>
      </Tippy>

      {mode === "open" &&
        isOpen &&
        section.items?.map(item => (
          <div key={item.name}>
            <div
              className={`sub-item ${activeItem === item.name ? "sub-active" : ""}`}
              onClick={() => (item.children ? onToggleSubMenu(item.name) : onItemClick(item))}
            >
              <span className="item-icon">{resolveItemIcon(item)}</span>
              <span className="item-label">{item.name}</span>
              {item.children && <DownOutlined className="submenu-arrow" />}
            </div>

            {item.children &&
              openSubMenus.includes(item.name) &&
              item.children.map(child => (
                <div
                  key={child.name}
                  className={`sub-sub-item ${activeItem === child.name ? "sub-sub-active" : ""}`}
                  onClick={() => onItemClick(child)}
                >
                  <span className="icon">{resolveItemIcon(child)}</span>
                  <span className="label">{child.name}</span>
                </div>
              ))}
          </div>
        ))}
    </div>
  );
};

export default SidebarSection;
